/**
 * Shared TanStack Query client for the portal.
 *
 * One instance lives at the app root (`App.tsx`), reached by every component
 * via `useQuery`/`useQueryClient`. This is the layer that gives us:
 *   - shared cache across components that query the same key (`['me']` etc.)
 *   - dedupe of in-flight requests
 *   - automatic refetch on window focus when data is stale
 *   - explicit invalidation hooks on auth state changes
 *
 * Convention: query keys are arrays, first element is the resource name.
 *   ['me']                  - current session/user
 *   ['games', filters]      - games catalog (filters is the query-params object)
 */

import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Auth state and shared data should refresh when the tab regains
      // focus — this replaces the bespoke window.addEventListener("focus")
      // wiring that used to live in SigninRoute.
      refetchOnWindowFocus: true,
      // One retry on failure. The API is small and idempotent; aggressive
      // retries don't add value and would hammer the server on outage.
      retry: 1,
      // Per-query overrides set higher times where appropriate
      // (e.g. ['me'] = 60s, ['games'] = 5min). 30s is the safe default.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
    },
  },
});
